// framework
import React, { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";

// styles
import styles from "./RoundSummary.module.scss";

export default function RoundSummary({ active = false, roundNumber = 1, players = [] }) {
    const [entered, setEntered] = useState(false);

    // Re-arm the enter animation whenever this layer becomes active (or the round changes)
    useEffect(() => {
        if (!active) { setEntered(false); return; }
        setEntered(false);
        const id = requestAnimationFrame(() => setEntered(true));
        return () => cancelAnimationFrame(id);
    }, [active, roundNumber]);

    const rounds = useMemo(() => Array.from({ length: 7 }, (_, i) => `Q${i + 1}`), []);

    return (
        <div className={styles.summaryRoot} data-testid="round-summary">
            <div className={`${styles.panel} ${styles.headerBar}`}>
                <span className={styles.headerText}>QUESTIONS REMAINING</span>
            </div>

            <div className={styles.questionsRow} data-entered={entered ? "true" : "false"}>
                {rounds.map((round, i) => {
                    const roundIdx = i + 1;
                    const state = roundIdx < roundNumber ? "past" : roundIdx === roundNumber ? "current" : "future";

                    return (
                        <div key={`round-${i}`} className={`${styles.panel} ${styles.qTile}`} data-state={state} aria-current={state === "current" ? "step" : undefined} title={round} >
                            <span className={styles.qLabel}>{round}</span>
                        </div>
                    );
                })}
            </div>

            <div className={styles.playersRow}>
                {players.filter(p => !p.mdmdData.waitingToJoin).map((player, i) => {
                    const isOut = Number(player?.mdmdData?.totalMoney) === 0;
                    const cash = getCashDisplay(player);
                    return (
                        <div key={`player-${i}`} className={styles.playerCard}>
                            <div className={styles.nameBox}>
                                <span className={styles.nameText}>{player.name}</span>
                            </div>
                            <div className={styles.cashBox} data-out={isOut ? "true" : "false"}>
                                <span className={styles.cashText}>{cash}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

RoundSummary.propTypes = {
    active: PropTypes.bool,
    roundNumber: PropTypes.number,
    players: PropTypes.array.isRequired,
};

// ---- helpers ----

function getCashDisplay(player) {
    const total = Number(player?.mdmdData?.totalMoney) || 0;
    if (total === 0) {
        const fr = Number(player?.mdmdData?.furthestRound) || 0;
        return `OUT${fr > 0 ? ` - Round ${fr}` : ""}`;
    }
    return formatCash(total);
}

function formatCash(n) {
    try {
        return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
    } catch {
        const num = Number(n) || 0;
        return "$" + num.toLocaleString("en-US");
    }
}
